import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header, { BreadcrumbItem } from '../components/Header';
import { usePageTracking } from '../hooks/usePageTracking';
import { meetingService, Meeting, MeetingMember } from '../services/meetingService';
import { getSiteConfig } from '../services/siteConfigService';
import {
  meetingCodeIsWorkshop2026,
  WORKSHOP_2026_PATH,
} from '../services/workshop2026Service';
import WorkshopFacilitatorSettings from '../components/workshop2026/WorkshopFacilitatorSettings';
import { useAuth } from '../context/AuthContext';
import { useDialog } from '../context/DialogContext';
import { t } from '../i18n/translate';
import styles from '../styles/meetingSettingsPage.module.css';

const JOIN_RULE_OPTIONS = [
  { value: 'anyone', label: 'Anyone (signed in)' },
  { value: 'meeting_key', label: 'Pass phrase required (default)' },
  { value: 'approval_required', label: 'Approval required' },
  { value: 'invite_only', label: 'Invite only' },
];

const FORMAT_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'in_person', label: 'In-Person' },
  { value: 'telemeeting', label: 'Telemeeting' },
];

const SERVICE_ROLE_OPTIONS = [
  'Secretary',
  'Treasurer',
  'Zoom Host',
  'Literature',
  'Greeter',
  'Intergroup Representative',
  'Newcomer Ambassador',
  'Scribe',
  'Speaker Seeker',
  'Sharing Coordinator',
  'Host',
] as const;

interface MeetingSettingsPageProps {
  user?: any;
  onLogout: () => void;
  onLogin: () => void;
}

const MeetingSettingsPage: React.FC<MeetingSettingsPageProps> = ({
  user,
  onLogout,
  onLogin,
}) => {
  const { code } = useParams<{ code: string }>();
  const { user: authUser } = useAuth();
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [format, setFormat] = useState('online');
  const [location, setLocation] = useState('');
  const [telemeetingDialIn, setTelemeetingDialIn] = useState('');
  const [telemeetingPasscode, setTelemeetingPasscode] = useState('');
  const [joinRule, setJoinRule] = useState('meeting_key');
  const [joinKeyInput, setJoinKeyInput] = useState('');
  const [customPassPhraseInput, setCustomPassPhraseInput] = useState('');
  const [members, setMembers] = useState<MeetingMember[]>([]);
  const [roleDrafts, setRoleDrafts] = useState<
    Record<string, { is_admin: boolean; is_manager: boolean; is_verified: boolean; service_roles: string[] }>
  >({});
  const [memberRoleSaving, setMemberRoleSaving] = useState<string | null>(null);
  const [canEditMeetingSettings, setCanEditMeetingSettings] = useState(false);
  const [phoneListVisibilityMode, setPhoneListVisibilityMode] = useState<'off' | 'verified_only' | 'managers_only'>('off');
  const [zoomUrl, setZoomUrl] = useState('');
  const [verificationDecisionSavingUserId, setVerificationDecisionSavingUserId] = useState<string | null>(null);
  const [siteWorkshopCode, setSiteWorkshopCode] = useState('');

  const effectiveUser = user || authUser;
  const { showConfirm } = useDialog();

  const isFlagshipWorkshop =
    meeting?.kind === 'workshop' && meetingCodeIsWorkshop2026(code, siteWorkshopCode);

  const reloadMeetingData = useCallback(async () => {
    if (!code || !effectiveUser) return;
    const m = await meetingService.getMeetingByCodeForAdmin(code);
    setMeeting(m);
    setName(m.name ?? '');
    setDescription(m.description ?? '');
    setZoomUrl((m.zoom_url as string) ?? '');
    setPhoneListVisibilityMode(
      (m.phone_list_visibility_mode as 'off' | 'verified_only' | 'managers_only' | undefined) ?? 'off'
    );
    const memberList = await meetingService.listMembers(code);
    setMembers(memberList);
  }, [code, effectiveUser]);

  useEffect(() => {
    getSiteConfig().then((site) => setSiteWorkshopCode(site.workshop_2026_meeting_code || ''));
  }, []);

  usePageTracking(`Meeting Settings: ${code || 'unknown'}`);

  useEffect(() => {
    if (!code || !effectiveUser) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [m, status] = await Promise.all([
          meetingService.getMeetingByCodeForAdmin(code),
          meetingService.getMemberStatus(code),
        ]);
        setMeeting(m);
        if (!status.is_member) {
          setError('You must be a member to view this page.');
          return;
        }
        setCanEditMeetingSettings(!!status.is_admin);
        setName(m.name ?? '');
        setDescription(m.description ?? '');
        setFormat((m.format as string) ?? 'online');
        setLocation(m.location ?? '');
        setTelemeetingDialIn(m.telemeeting_dial_in ?? '');
        setTelemeetingPasscode(m.telemeeting_passcode ?? '');
        setJoinRule((m.join_rule as string) ?? 'meeting_key');
        setJoinKeyInput((m.join_key as string) ?? '');
        setPhoneListVisibilityMode(
          (m.phone_list_visibility_mode as 'off' | 'verified_only' | 'managers_only' | undefined) ?? 'off'
        );
        setZoomUrl((m.zoom_url as string) ?? '');
        const memberList = await meetingService.listMembers(code);
        setMembers(memberList);
        const drafts: Record<string, { is_admin: boolean; is_manager: boolean; is_verified: boolean; service_roles: string[] }> =
          {};
        memberList.forEach((mm) => {
          drafts[mm.user_id] = {
            is_admin: !!mm.is_admin,
            is_manager: !!mm.is_manager,
            is_verified: !!mm.is_verified,
            service_roles: Array.isArray(mm.service_roles) ? mm.service_roles : [],
          };
        });
        setRoleDrafts(drafts);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Meeting not found');
        setMeeting(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [code, effectiveUser]);

  const getDraft = (memberId: string) => {
    return (
      roleDrafts[memberId] || {
        is_admin: false,
        is_manager: false,
        is_verified: false,
        service_roles: [],
      }
    );
  };

  const toggleDraftRole = (memberId: string, role: string, checked: boolean) => {
    setRoleDrafts((prev) => {
      const current = prev[memberId] || {
        is_admin: false,
        is_manager: false,
        is_verified: false,
        service_roles: [],
      };
      const nextRoles = new Set(current.service_roles);
      if (checked) nextRoles.add(role);
      else nextRoles.delete(role);
      return {
        ...prev,
        [memberId]: {
          ...current,
          service_roles: Array.from(nextRoles),
        },
      };
    });
  };

  const handleSaveMemberRoles = async (member: MeetingMember) => {
    if (!code || !effectiveUser) return;
    const draft = getDraft(member.user_id);
    setMemberRoleSaving(member.user_id);
    setSaveMessage(null);
    setError(null);
    try {
      const updates: Partial<{
        is_admin: boolean;
        is_manager: boolean;
        is_verified: boolean;
        service_roles: string[];
      }> = {
        is_verified: draft.is_verified,
        service_roles: draft.service_roles,
      };
      if (canEditMeetingSettings) {
        updates.is_admin = draft.is_admin;
        updates.is_manager = draft.is_manager;
      }

      const updated = await meetingService.updateMemberRoles(code, member.user_id, updates);
      setMembers((prev) => prev.map((m) => (m.user_id === member.user_id ? updated : m)));
      setRoleDrafts((prev) => ({
        ...prev,
        [member.user_id]: {
          is_admin: updated.is_admin,
          is_manager: updated.is_manager,
          is_verified: updated.is_verified,
          service_roles: updated.service_roles,
        },
      }));
      setSaveMessage('Member roles saved.');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save member roles');
    } finally {
      setMemberRoleSaving(null);
    }
  };

  const handleDecideVerification = async (member: MeetingMember, approve: boolean) => {
    if (!code || !effectiveUser) return;
    setVerificationDecisionSavingUserId(member.user_id);
    setSaveMessage(null);
    setError(null);
    try {
      const updated = await meetingService.decideMeetingVerification(code, member.user_id, approve);
      setMembers((prev) => prev.map((m) => (m.user_id === member.user_id ? updated : m)));
      setRoleDrafts((prev) => ({
        ...prev,
        [member.user_id]: {
          is_admin: updated.is_admin,
          is_manager: updated.is_manager,
          is_verified: updated.is_verified,
          service_roles: updated.service_roles,
        },
      }));
      setSaveMessage(approve ? 'Verification approved.' : 'Verification denied.');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update verification');
    } finally {
      setVerificationDecisionSavingUserId(null);
    }
  };

  const backPath = isFlagshipWorkshop
    ? `${WORKSHOP_2026_PATH}?tab=workshop`
    : `/meetings/${code}`;

  const handleRemoveMember = (member: { user_id: string; display_name: string }) => {
    showConfirm({
      title: t('meetings.dialog.remove_member_title'),
      message: t('meetings.dialog.remove_member_message', { name: member.display_name }),
      confirmText: t('meetings.dialog.remove_member_confirm'),
      cancelText: t('dialog.cancel'),
      variant: 'danger',
      onConfirm: async () => {
        if (!code) return;
        if (!canEditMeetingSettings) {
          setError('Only meeting admins can remove members.');
          return;
        }
        await meetingService.removeMember(code, member.user_id);
        setMembers((prev) => prev.filter((m) => m.user_id !== member.user_id));
      },
    });
  };

  const handleCopyJoinLink = () => {
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    const url = `${base}/meeting/join/${encodeURIComponent((meeting?.meeting_code || code || '').toUpperCase())}/${encodeURIComponent((meeting?.join_key || joinKeyInput || '').toUpperCase())}`;
    navigator.clipboard.writeText(url).then(() => setSaveMessage('Join link copied to clipboard.'));
  };

  const handleRegeneratePassPhrase = async () => {
    if (!code || saving || !canEditMeetingSettings) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      await meetingService.updateMeeting(code, { regenerate_pass_phrase: true });
      const m = await meetingService.getMeetingByCodeForAdmin(code);
      setMeeting(m);
      setJoinKeyInput((m.join_key as string) ?? '');
      setCustomPassPhraseInput('');
      setSaveMessage('Pass phrase regenerated. Share the new join link.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code || saving) return;
    if (!canEditMeetingSettings) {
      setError('Only meeting admins can change meeting settings.');
      return;
    }
    setSaving(true);
    setSaveMessage(null);
    try {
      await meetingService.updateMeeting(code, {
        name: name.trim() || (meeting?.name ?? ''),
        description: description.trim(),
        format,
        location: location.trim(),
        telemeeting_dial_in: format === 'telemeeting' ? telemeetingDialIn.trim() : undefined,
        telemeeting_passcode: format === 'telemeeting' ? telemeetingPasscode.trim() : undefined,
        join_rule: joinRule,
        ...(customPassPhraseInput.trim() ? { join_key: customPassPhraseInput.trim() } : {}),
        phone_list_visibility_mode: phoneListVisibilityMode,
        ...((meeting?.kind === 'workshop' || format === 'online') ? { zoom_url: zoomUrl.trim() } : {}),
      });
      setSaveMessage('Settings saved.');
      if (customPassPhraseInput.trim()) {
        const m = await meetingService.getMeetingByCodeForAdmin(code);
        setMeeting(m);
        setJoinKeyInput((m.join_key as string) ?? '');
        setCustomPassPhraseInput('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const programId = (meeting?.program_id || 'saa').toLowerCase();
  const programLabel = (meeting?.program_id || 'SAA').toUpperCase();
  const formatLabel = (fmt: string | undefined) =>
    ({ online: 'Online', hybrid: 'Hybrid', in_person: 'In-Person', telemeeting: 'Telemeeting' }[fmt || 'online'] || 'Online');
  const meetingBreadcrumbLabel = meeting
    ? `${(meeting.name || '').trim() || formatLabel(meeting.format)} (${meeting.meeting_code})`
    : (code || 'Meeting');
  const breadcrumbs: BreadcrumbItem[] = isFlagshipWorkshop
    ? [
        { label: 'Home', path: '/', active: false },
        { label: 'SAA', path: '/saa', active: false },
        { label: '2026 Step Workshop', path: WORKSHOP_2026_PATH, active: false },
        { label: 'Workshop settings', path: '', active: true },
      ]
    : [
        { label: 'Home', path: '/', active: false },
        { label: programLabel, path: `/${programId}`, active: false },
        { label: 'Meetings', path: `/${programId}?tab=meetings`, active: false },
        { label: meetingBreadcrumbLabel, path: backPath, active: false },
        { label: 'Settings', path: '', active: true },
      ];

  if (!effectiveUser) {
    return (
      <div className={styles.page}>
        <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />
        <main className={styles.main}>
          <p className={styles.error}>Sign in to view meeting settings.</p>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />
        <main className={styles.main}>
          <p className={styles.loading}>Loading…</p>
        </main>
      </div>
    );
  }

  if (error || !meeting) {
    return (
      <div className={styles.page}>
        <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />
        <main className={styles.main}>
          <p className={styles.error}>{error || 'Meeting not found'}</p>
          <a href={backPath} className={styles.backLink}>← Back to meeting</a>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />
      <main className={styles.main}>
        <h1 className={styles.title}>{isFlagshipWorkshop ? 'Workshop settings' : 'Meeting settings'}</h1>
        {isFlagshipWorkshop ? (
          <>
            <WorkshopFacilitatorSettings
              meeting={meeting}
              meetingCode={code!}
              members={members}
              canEdit={canEditMeetingSettings}
              onReload={reloadMeetingData}
            />
            <p className={styles.formActions} style={{ marginTop: '1.5rem' }}>
              <Link to={backPath} className={styles.backLink}>
                ← Back to workshop
              </Link>
            </p>
          </>
        ) : (
        <>
        <form onSubmit={handleSubmit} className={styles.form}>
          <label className={styles.label}>Meeting name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={styles.input}
            required
          />
          <label className={styles.label}>Description (optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={styles.textarea}
            rows={3}
          />
          <label className={styles.label}>Format</label>
          <select value={format} onChange={(e) => setFormat(e.target.value)} className={styles.select}>
            {FORMAT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {(format === 'hybrid' || format === 'in_person') && (
            <>
              <label className={styles.label}>Location</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className={styles.input}
              />
            </>
          )}
          {format === 'telemeeting' && (
            <>
              <label className={styles.label}>Dial-in number</label>
              <input
                type="text"
                value={telemeetingDialIn}
                onChange={(e) => setTelemeetingDialIn(e.target.value)}
                className={styles.input}
              />
              <label className={styles.label}>Passcode (optional)</label>
              <input
                type="text"
                value={telemeetingPasscode}
                onChange={(e) => setTelemeetingPasscode(e.target.value)}
                className={styles.input}
              />
            </>
          )}
          {(meeting?.kind === 'workshop' || format === 'online') && (
            <>
              <label className={styles.label}>Zoom meeting URL</label>
              <input
                type="url"
                value={zoomUrl}
                onChange={(e) => setZoomUrl(e.target.value)}
                className={styles.input}
                placeholder="https://zoom.us/j/..."
                disabled={!canEditMeetingSettings}
              />
              <p className={styles.intro} style={{ marginTop: '-0.25rem', fontSize: '0.85rem' }}>
                Visible to confirmed workshop members on the 2026 workshop page. Leave blank until ready.
              </p>
            </>
          )}
          <label className={styles.label}>Who can join?</label>
          <select
            value={joinRule}
            onChange={(e) => setJoinRule(e.target.value)}
            className={styles.select}
          >
            {JOIN_RULE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <label className={styles.label}>Phone list visibility</label>
          <select
            value={phoneListVisibilityMode}
            onChange={(e) =>
              setPhoneListVisibilityMode(e.target.value as 'off' | 'verified_only' | 'managers_only')
            }
            disabled={!canEditMeetingSettings}
            className={styles.select}
          >
            <option value="off">Off</option>
            <option value="verified_only">Verified participants</option>
            <option value="managers_only">Managers & admins</option>
          </select>
          {joinRule === 'meeting_key' && canEditMeetingSettings && (
            <div className={styles.passPhraseBlock}>
              <p className={styles.passPhraseIntro}>
                Share the join link with people who should join; the link includes the pass phrase so they don&apos;t need to type it.
              </p>
              <div className={styles.joinLinkRow}>
                <code className={styles.joinUrl}>
                  {typeof window !== 'undefined'
                    ? `${window.location.origin}/meeting/join/${(meeting?.meeting_code || code || '').toUpperCase()}/${(meeting?.join_key || joinKeyInput || '').toUpperCase()}`
                    : ''}
                </code>
                <button type="button" onClick={handleCopyJoinLink} className={styles.copyLinkBtn}>
                  Copy join link
                </button>
              </div>
              <p className={styles.passPhraseHint}>Pass phrase: <strong>{(meeting?.join_key || joinKeyInput || '').toUpperCase() || '—'}</strong></p>
              <div className={styles.changePassPhraseRow}>
                <input
                  type="text"
                  value={customPassPhraseInput}
                  onChange={(e) => setCustomPassPhraseInput(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
                  placeholder="Custom pass phrase (optional)"
                  className={styles.input}
                  autoComplete="off"
                />
                <button type="button" onClick={handleRegeneratePassPhrase} disabled={saving || !canEditMeetingSettings} className={styles.regenerateBtn}>
                  Regenerate
                </button>
              </div>
            </div>
          )}
          {saveMessage && <p className={styles.saveMessage}>{saveMessage}</p>}
          <div className={styles.formActions}>
            <button type="submit" className={styles.submitBtn} disabled={saving || !canEditMeetingSettings}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            <a href={backPath} className={styles.backLink}>← Back to meeting</a>
          </div>
        </form>

        <section className={styles.membersSection}>
          <h2 className={styles.membersTitle}>Members & permissions</h2>
          <p className={styles.membersIntro}>Meeting admins and managers can review roles and verification requests. Admins can also remove members.</p>
          {members.length === 0 ? (
            <p className={styles.membersEmpty}>No members yet.</p>
          ) : (
            <ul className={styles.membersList}>
              {members.map((m) => (
                <li key={m.user_id} className={styles.memberRow}>
                  <span className={styles.memberName}>{m.display_name}</span>
                  {m.is_admin && <span className={styles.adminBadge}>Admin</span>}
                  {m.is_manager && <span className={styles.managerBadge}>Manager</span>}
                  {m.is_verified && <span className={styles.verifiedBadge}>Verified</span>}
                  {m.service_roles && m.service_roles.length > 0 && (
                    <span className={styles.serviceRolesBadge}>{m.service_roles.join(', ')}</span>
                  )}

                  {m.verification_requested_at && !m.is_verified && (
                    <div className={styles.verificationDecisionRow}>
                      <span className={styles.pendingBadge}>Pending verification</span>
                      <button
                        type="button"
                        className={styles.approveBtn}
                        disabled={verificationDecisionSavingUserId === m.user_id}
                        onClick={() => handleDecideVerification(m, true)}
                      >
                        {verificationDecisionSavingUserId === m.user_id ? 'Working…' : 'Approve'}
                      </button>
                      <button
                        type="button"
                        className={styles.denyBtn}
                        disabled={verificationDecisionSavingUserId === m.user_id}
                        onClick={() => handleDecideVerification(m, false)}
                      >
                        {verificationDecisionSavingUserId === m.user_id ? 'Working…' : 'Deny'}
                      </button>
                    </div>
                  )}

                  <details className={styles.roleEditDetails}>
                    <summary>Edit roles</summary>
                    <div className={styles.roleEditBody}>
                      <label className={styles.roleCheckbox}>
                        <input
                          type="checkbox"
                          checked={getDraft(m.user_id).is_admin}
                          disabled={m.user_id === effectiveUser?.email || !canEditMeetingSettings}
                          onChange={(e) =>
                            setRoleDrafts((prev) => ({
                              ...prev,
                              [m.user_id]: { ...getDraft(m.user_id), is_admin: e.target.checked },
                            }))
                          }
                        />
                        Admin
                      </label>
                      <label className={styles.roleCheckbox}>
                        <input
                          type="checkbox"
                          checked={getDraft(m.user_id).is_manager}
                          disabled={!canEditMeetingSettings}
                          onChange={(e) =>
                            setRoleDrafts((prev) => ({
                              ...prev,
                              [m.user_id]: { ...getDraft(m.user_id), is_manager: e.target.checked },
                            }))
                          }
                        />
                        Manager
                      </label>
                      <label className={styles.roleCheckbox}>
                        <input
                          type="checkbox"
                          checked={getDraft(m.user_id).is_verified}
                          onChange={(e) =>
                            setRoleDrafts((prev) => ({
                              ...prev,
                              [m.user_id]: { ...getDraft(m.user_id), is_verified: e.target.checked },
                            }))
                          }
                        />
                        Verified
                      </label>

                      <div className={styles.serviceRolesGrid} aria-label="Service roles">
                        {SERVICE_ROLE_OPTIONS.map((role) => (
                          <label key={role} className={styles.roleCheckbox}>
                            <input
                              type="checkbox"
                              checked={(getDraft(m.user_id).service_roles || []).includes(role)}
                              onChange={(e) => toggleDraftRole(m.user_id, role, e.target.checked)}
                            />
                            {role}
                          </label>
                        ))}
                      </div>

                      <button
                        type="button"
                        className={styles.saveRolesBtn}
                        disabled={memberRoleSaving === m.user_id}
                        onClick={() => handleSaveMemberRoles(m)}
                      >
                        {memberRoleSaving === m.user_id ? 'Saving…' : 'Save roles'}
                      </button>
                    </div>
                  </details>

                  {canEditMeetingSettings && m.user_id !== effectiveUser?.email && (
                    <button
                      type="button"
                      className={styles.removeMemberBtn}
                      onClick={() => handleRemoveMember(m)}
                    >
                      Remove from meeting
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
        </>
        )}
      </main>
    </div>
  );
};

export default MeetingSettingsPage;
